import { length, lt, map } from 'ramda';
import React, { FC } from 'react';

import BaseCard from '@ui/atoms/BaseCard';
import BasicText from '@ui/atoms/BasicText';
import FlexColumn, { IFlexColumn } from '@ui/atoms/FlexColumn';
import HeadingText from '@ui/atoms/HeadingText';
import LoadingWrapper from '@ui/atoms/LoadingWrapper';
import Skeleton from '@ui/atoms/Skeleton';

import DashedLinkButton from '@ui/molecules/DashedLinkButton';
import FineprintBox from '@ui/molecules/FineprintBox';

import fullWidth from '@ui/styled/FullWidth';
import spaced from '@ui/styled/Spaced';

import { IPageProps } from '@folx-types/flow-steps';

import { IDependent } from './DependentsList.page';

export interface IDependentsListPageContent {
	pageHeader: string;
	supportText: string;
	inviteLabel: string;
}

interface IDependentsListProps extends IPageProps {
	pageContent: IDependentsListPageContent;
	dependents?: IDependent[];
	loading?: boolean;
}

const SpacedHeading = spaced(HeadingText);
const FullWidthColumn = fullWidth<IFlexColumn>(FlexColumn);
const maxDependents = 5;

const ListSkeleton = () => (
	<>
		<Skeleton height="xxl" fullWidth />
		<Skeleton height="xxl" fullWidth />
		<Skeleton height="xxl" fullWidth />
	</>
);

const Dependents: FC<{ list: IDependent[] }> = ({ list }) =>
	map(
		(dependent) => (
			<BaseCard key={dependent.id} shadow borderless padding="smmd" radius="md">
				<BasicText>{dependent.email}</BasicText>
			</BaseCard>
		),
		list
	);

const DependentsList: FC<IDependentsListProps> = ({
	dependents = [],
	loading = false,
	pageContent,
}) => {
	const { pageHeader, supportText, inviteLabel } = pageContent;

	return (
		<FullWidthColumn gap="sm">
			<SpacedHeading marginBottom="xs">{pageHeader}</SpacedHeading>
			<FineprintBox isWysiwyg finePrint={supportText} />

			<LoadingWrapper loading={loading} skeleton={<ListSkeleton />}>
				<Dependents list={dependents} />
				{lt(length(dependents), maxDependents) && (
					<DashedLinkButton
						label={inviteLabel}
						href="/app/member/dependents/invite"
						variant="small"
					/>
				)}
			</LoadingWrapper>
		</FullWidthColumn>
	);
};

export default DependentsList;
